import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ListItem from '@mui/material/ListItem';
import Chip from '@mui/material/Chip';

import { bandChipSx, bandTone, normalizeSortOrder } from './assessmentUiUtils';
import { getUniqueScoreValues, scoreToneFromValue, scoreToggleButtonSx } from './scoreUtils';

function pickBandLabel(total, bandsSorted) {
  if (!Number.isFinite(Number(total))) return null;
  const t = Number(total);

  for (const b of bandsSorted || []) {
    if (!b) continue;
    const mn = b.min_score === null || b.min_score === undefined ? null : Number(b.min_score);
    const mx = b.max_score === null || b.max_score === undefined ? null : Number(b.max_score);

    const geMin = mn === null ? true : t >= mn;
    const leMax = mx === null ? true : t <= mx;
    if (geMin && leMax) return b.label || null;
  }
  return null;
}

const StudentRow = ({
  student,
  sortedCriteria,
  focusedCriterionId,
  result,
  itemsByKey,
  onSetScore,
  readOnly,
  isSavingScore,
  bands,
}) => {
  const studentId = student?.id;

  const bandsSorted = useMemo(
    () => (bands || []).slice().sort((a, b) => normalizeSortOrder(a?.sort_order) - normalizeSortOrder(b?.sort_order)),
    [bands]
  );

  const studentSummary = useMemo(() => {
    const crit = Array.isArray(sortedCriteria) ? sortedCriteria : [];
    const criterionIds = crit.filter((c) => c && c.id != null).map((c) => String(c.id));
    const totalExpected = criterionIds.length;
    if (!studentId || totalExpected === 0) return null;

    let total = 0;
    let recorded = 0;

    for (const cid of criterionIds) {
      const k = `${studentId}:${cid}`;
      const score = itemsByKey?.[String(k)]?.score;
      const hasScore = score !== null && score !== undefined && score !== '' && Number.isFinite(Number(score));
      if (!hasScore) continue;
      recorded += 1;
      total += Number(score);
    }

    const missing = Math.max(0, totalExpected - recorded);
    const totalScore = readOnly && result?.total_score !== undefined && result?.total_score !== null
      ? Number(result.total_score)
      : total;

    const bandLabel = (readOnly && result?.band_label)
      ? String(result.band_label)
      : pickBandLabel(totalScore, bandsSorted);

    return {
      totalScore,
      recorded,
      missing,
      totalExpected,
      bandLabel,
    };
  }, [studentId, sortedCriteria, itemsByKey, readOnly, result?.total_score, result?.band_label, bandsSorted]);

  const filteredCriteria = useMemo(() => {
    const crit = Array.isArray(sortedCriteria) ? sortedCriteria : [];
    if (!focusedCriterionId) return crit;
    return crit.filter((c) => String(c?.id) === String(focusedCriterionId));
  }, [sortedCriteria, focusedCriterionId]);

  return (
    <ListItem divider sx={{ px: 0, py: 1 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {String(student?.student_no ?? student?.id ?? '')} — {String(student?.first_name || '')}{' '}
              {String(student?.last_name || '')}
            </Typography>
            {studentSummary ? (
              <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip size="small" label={`Toplam: ${String(studentSummary.totalScore)}`} />
                {studentSummary.bandLabel ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={bandTone(studentSummary.bandLabel)}
                    sx={bandChipSx(bandTone(studentSummary.bandLabel))}
                    label={String(studentSummary.bandLabel)}
                  />
                ) : null}
                <Chip size="small" label={`Kayıt: ${studentSummary.recorded}/${studentSummary.totalExpected}`} />
                <Chip
                  size="small"
                  label={`Eksik: ${studentSummary.missing}`}
                  color={studentSummary.missing === 0 ? 'success' : 'warning'}
                />
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredCriteria.map((c) => {
            const key = `${studentId}:${c?.id}`;
            const item = itemsByKey?.[String(key)] || {};

            const scoreValues = Array.isArray(c?.score_values) ? c.score_values : [0, 1, 2, 3];
            const uniqScoreValues = getUniqueScoreValues(scoreValues, [0, 1, 2, 3]);

            const currentScore = Number.isFinite(Number(item?.score)) ? Number(item.score) : null;
            const isSavingThis = isSavingScore ? !!isSavingScore(studentId, c?.id) : false;

            return (
              <Paper key={c?.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                {c?.section_title ? (
                  <Typography variant="caption" color="text.secondary">
                    {String(c.section_title || '')}
                  </Typography>
                ) : null}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    mt: c?.section_title ? 0.5 : 0,
                  }}
                >
                  <Typography sx={{ flex: 1, minWidth: 220 }}>{String(c?.name || '')}</Typography>

                  {uniqScoreValues.length > 0 ? (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={currentScore}
                      onChange={(_, v) => onSetScore && onSetScore(studentId, c?.id, v)}
                      disabled={readOnly || isSavingThis}
                      sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1.25 } }}
                    >
                      {uniqScoreValues.map((v) => (
                        <ToggleButton
                          key={String(v)}
                          value={Number(v)}
                          sx={scoreToggleButtonSx(scoreToneFromValue(v, uniqScoreValues))}
                        >
                          {String(v)}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  ) : (
                    <Autocomplete
                      size="small"
                      options={scoreValues}
                      value={currentScore}
                      onChange={(_, v) => onSetScore && onSetScore(studentId, c?.id, v)}
                      disabled={readOnly || isSavingThis}
                      sx={{ width: 140 }}
                      renderInput={(params) => <TextField {...params} label="Puan" />}
                    />
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    </ListItem>
  );
};

export default StudentRow;
